// Who is signed in, and the things you can do about it.
//
// An avatar in the top-right corner, because that is where people look and
// because the header is a working strip on a clinical viewer — a button wide
// enough to hold an email address was taking room from the study it sits above.
// The address is still there, in the menu, where it can be read in full instead
// of truncated to fit.
//
// Workspaces lives in here rather than in the header. It is where you go to
// change who can see your slides, which is an account concern rather than a
// place you move between while reading one — and the header of a clinical
// viewer is working space that the study it sits above has better uses for.
//
// There IS an account page now, and this is how you reach it. The comment here
// used to explain why there was not one — names arrived with signup, so a place
// to correct them had to arrive too.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LogOut, ShieldCheck, User, Users, FolderOpen } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { currentSession, signOut } from '../../auth/passwordSession';

/**
 * One or two letters for the avatar.
 *
 * From a display name when it looks like a name — "Ada Lovelace" gives AL —
 * and otherwise from the address, where the local part is the only half worth
 * reading: every colleague at one institution shares the domain, so initials
 * taken from it would be identical for everybody.
 *
 * Two letters, and that is the whole of what this discloses. The menu itself
 * deliberately shows a NAME and never the address; an avatar has to say
 * something, and "AC" is not an email address.
 */
function initials(displayName: string | null, email: string | null): string {
  const source = (displayName ?? email ?? '').trim();
  if (!source) return '?';
  const local = source.includes('@') ? source.split('@')[0] : source;
  const words = local.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export function AccountControl(): JSX.Element | null {

  // Hooks run unconditionally — the early return lives below them, because
  // bailing before useQuery would change the hook order between renders the
  // moment a deployment's config differs.
  const { data } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: currentSession,
    enabled: true,
    // The session outlives any page, and a 401 anywhere else is what actually
    // surfaces its end — so there is nothing to gain from refetching this.
    staleTime: Infinity,
    retry: false,
  });

  // Every deployment authenticates, so there is always an account to show once
  // the query resolves.
  if (!data) return null;

  /**
   * A NAME, never the address.
   *
   * The address is personal data, and this menu sits open on a screen in a
   * shared reading room, over a colleague's shoulder, and in every screenshot
   * anybody takes of the viewer. It told them nothing the name does not.
   *
   * display_name still HOLDS the address for accounts that predate the name
   * fields, or that an identity provider gave us nothing better for — so it is
   * compared against the address rather than trusted, and an account with no
   * real name gets a prompt to add one instead of its mailbox printed at the
   * top of the menu.
   */
  const named = data.displayName && data.displayName !== data.email ? data.displayName : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // aria-label rather than a visible one: the avatar is initials, which
          // a screen reader would otherwise announce as two stray letters.
          //
          // The NAME, or nothing — this used to interpolate the address, which
          // is the same disclosure as printing it, read aloud.
          aria-label={named ? `Account: ${named}` : 'Account'}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary outline-none transition-colors hover:bg-primary/25 focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:bg-primary/25"
        >
          {initials(data.displayName, data.email)}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {named ? (
            <div className="truncate text-sm font-medium text-foreground">{named}</div>
          ) : (
            // No name on file. Saying so beats falling back to the address, and
            // it points at the screen that fixes it.
            <div className="text-sm font-medium text-muted-foreground">Signed in</div>
          )}
          {/* Only where it is true. Most people are not superadmins, and a
              badge that everyone carries tells nobody anything. */}
          {data.superadmin && (
            <div className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
              <ShieldCheck className="h-3 w-3" />
              Superadmin
            </div>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* asChild so this is a real anchor: middle-click, cmd-click and
            "open in new tab" all work, which a div with an onSelect handler
            silently swallows. */}
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <User className="h-4 w-4" />
            Your account
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/workspaces">
            <Users className="h-4 w-4" />
            Workspaces
          </Link>
        </DropdownMenuItem>

        {/* Beside Workspaces rather than in the header: the workspace selector
            already governs every view, and a second global control deciding
            what you are looking at is the confusion this avoids. */}
        <DropdownMenuItem asChild>
          <Link to="/collections">
            <FolderOpen className="h-4 w-4" />
            Collections
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
